<template>
  <div class="home">
    <Navbar :is-active="1"></Navbar>
    <div class="body form-page-body">
      <CommercialSidebar active="facturacion" />
      <div class="body__containt form-page-content">
        <div class="page__title form-page-title">
          <open-arrow></open-arrow>
          <div class="page__title-text">
            <h2>Facturación electrónica</h2>
            <p class="page__lead">
              URL, token y series se guardan por empresa. Cada socio comercial emite con su propia cuenta Nubefact.
            </p>
          </div>
        </div>

        <div v-if="loading" class="form-card">
          <p style="margin:0; color:#888;">Cargando configuración…</p>
        </div>

        <form v-else class="form-card" @submit.prevent="save">
          <div class="form-card__header">
            <div class="form-card__icon">
              <i class="fas fa-file-invoice"></i>
            </div>
            <div>
              <h3>Configuración Nubefact de la empresa</h3>
              <p>Los cambios aplican a la empresa activa de la sesión.</p>
            </div>
          </div>

          <div class="form-grid">
            <div class="field">
              <span class="field__label">Estado</span>
              <div class="status-toggle">
                <button type="button" :class="{ active: form.enabled }" @click="form.enabled = true">Activo</button>
                <button type="button" :class="{ active: !form.enabled }" @click="form.enabled = false">Inactivo</button>
              </div>
            </div>

            <label class="field">
              <span class="field__label">IGV (%)</span>
              <input type="number" v-model.number="form.igv" min="0" max="100" step="0.01" />
            </label>

            <label class="field">
              <span class="field__label">Serie factura</span>
              <input type="text" v-model="form.serie_factura" maxlength="4" placeholder="FFF1" />
            </label>

            <label class="field">
              <span class="field__label">Serie boleta</span>
              <input type="text" v-model="form.serie_boleta" maxlength="4" placeholder="BBB1" />
            </label>

            <label class="field field--full">
              <span class="field__label">Ruta API (URL Nubefact)</span>
              <input type="text" v-model="form.url" placeholder="https://api.nubefact.com/api/v1/..." />
            </label>

            <label class="field field--full">
              <span class="field__label">
                Token
                <em v-if="meta.has_token">— configurado: {{ meta.token_masked }}</em>
              </span>
              <input
                type="password"
                v-model="form.token"
                :placeholder="meta.has_token ? 'Dejar vacío para conservar el token actual' : 'Pega el token de Nubefact'"
                autocomplete="new-password"
              />
            </label>
          </div>

          <div class="meta-row">
            <div class="meta-item">
              <span>Origen</span>
              <strong>{{ meta.configured ? 'Configuración de empresa' : 'Fallback global (.env)' }}</strong>
            </div>
            <div class="meta-item">
              <span>Company ID</span>
              <strong>{{ companyId || '—' }}</strong>
            </div>
          </div>

          <div class="form-actions">
            <button type="submit" class="btn btn--primary" :disabled="saving">
              <i class="fas fa-save"></i>
              {{ saving ? 'Guardando…' : 'Guardar configuración' }}
            </button>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import Navbar from '@/components/NavBar.vue'
import CommercialSidebar from '@/components/CommercialSidebar.vue'
import OpenArrow from '@/components/OpenArrow.vue'
import '@/static/css/form-card.css'

export default {
  name: 'FacturacionElectronica',
  components: { Navbar, CommercialSidebar, OpenArrow },
  data() {
    return {
      loading: true,
      saving: false,
      blocked: false,
      companyId: parseInt(localStorage.getItem('company_id'), 10),
      form: {
        enabled: false,
        url: '',
        token: '',
        serie_factura: 'FFF1',
        serie_boleta: 'BBB1',
        igv: 18
      },
      meta: {
        has_token: false,
        token_masked: '',
        source: '',
        configured: false
      }
    }
  },
  created() {
    if (!localStorage.getItem('token')) {
      this.$router.push('/login')
    }
    const role = localStorage.getItem('role')
    if (role === 'admin' && localStorage.getItem('admin_in_company') !== '1') {
      this.blocked = true
      this.$swal({
        icon: 'info',
        title: 'Selecciona un socio comercial',
        text: 'Entra al detalle del cliente para configurar su facturación electrónica.'
      })
      this.$router.push('/comercial')
      return
    }
    if (role === 'admin' && !localStorage.getItem('company_id')) {
      this.blocked = true
      this.$router.push('/comercial')
    }
  },
  mounted() {
    if (!this.blocked) this.load()
  },
  methods: {
    cleanUrl(url) {
      return String(url || '').trim().replace(/[,;]+$/g, '').trim()
    },
    async load() {
      if (!this.companyId) {
        this.loading = false
        this.$swal({ icon: 'warning', title: 'No hay empresa seleccionada en la sesión.' })
        return
      }
      this.loading = true
      try {
        const res = await axios.get('general/company/' + this.companyId + '/nubefact')
        const data = (res.data && res.data.data) || {}
        this.form.enabled = !!data.enabled
        this.form.url = this.cleanUrl(data.url)
        this.form.token = ''
        this.form.serie_factura = data.serie_factura || 'FFF1'
        this.form.serie_boleta = data.serie_boleta || 'BBB1'
        this.form.igv = data.igv != null ? Number(data.igv) : 18
        this.meta.has_token = !!data.has_token
        this.meta.token_masked = data.token_masked || ''
        this.meta.source = data.source || ''
        this.meta.configured = !!data.configured
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'No se pudo cargar la configuración.'
        this.$swal({ icon: 'error', title: msg })
      } finally {
        this.loading = false
      }
    },
    async save() {
      if (!this.companyId) return
      this.form.url = this.cleanUrl(this.form.url)
      if (this.form.enabled && !this.form.url) {
        this.$swal({ icon: 'warning', title: 'La URL de Nubefact es obligatoria si activas la facturación.' })
        return
      }
      if (this.form.enabled && !this.form.token && !this.meta.has_token) {
        this.$swal({ icon: 'warning', title: 'Debes indicar el token de Nubefact.' })
        return
      }

      this.saving = true
      try {
        const payload = {
          enabled: !!this.form.enabled,
          url: this.form.url,
          serie_factura: (this.form.serie_factura || '').toUpperCase(),
          serie_boleta: (this.form.serie_boleta || '').toUpperCase(),
          igv: this.form.igv
        }
        if (this.form.token && this.form.token.trim() !== '') {
          payload.token = this.form.token.trim()
        }

        const res = await axios.put('general/company/' + this.companyId + '/nubefact', payload)
        const data = (res.data && res.data.data) || {}
        this.form.token = ''
        this.meta.has_token = !!data.has_token
        this.meta.token_masked = data.token_masked || ''
        this.meta.source = data.source || 'company'
        this.meta.configured = !!data.configured
        this.form.enabled = !!data.enabled
        this.form.url = this.cleanUrl(data.url || this.form.url)
        this.form.serie_factura = data.serie_factura || this.form.serie_factura
        this.form.serie_boleta = data.serie_boleta || this.form.serie_boleta
        this.form.igv = data.igv != null ? Number(data.igv) : this.form.igv

        this.$swal({
          icon: 'success',
          title: (res.data && res.data.message) || 'Configuración guardada.',
          timer: 1800,
          showConfirmButton: false
        })
      } catch (e) {
        const msg = (e.response && e.response.data && e.response.data.message) || 'No se pudo guardar.'
        this.$swal({ icon: 'error', title: msg })
      } finally {
        this.saving = false
      }
    }
  }
}
</script>
